import { createContext, type ReactNode, useContext, useState } from "react";
import ActivitiesStore from "../activities/ActivitiesStore";
import BusinessCustomersStore from "../business/admin/customers/BusinessCustomersStore";
import BusinessTemplatesStore from "../business/admin/templates/BusinessTemplatesStore";
import BusinessCustomerMailsStore from "../business/admindev/mails/BusinessCustomerMailsStore";
import BusinessFeedItemsStore from "../business/feed/BusinessFeedItemsStore";
import BusinessFeedCommentsStore from "../business/feedcomment/BusinessFeedCommentsStore";
import BusinessFeedCommentsReplyStore from "../business/feedcommentreply/BusinessFeedCommentsReplyStore";
import BusinessModulesStore from "../business/modules/BusinessModulesStore";
import BusinessPlansStore from "../business/plan/BusinessPlansStore";
import BusinessScheduleStore from "../business/schedule/BusinessScheduleStore";
import DevsStore from "../dev/DevsStore";
import FeedbackStore from "../feedback/FeedbackStore";
import ModulesDataStore from "../modules/core/ModulesDataStore";
import ModulesStore from "../modules/core/ModulesStore";
import PlansStore from "../plans/PlansStore";
import SectionsStore from "../sections/SectionsStore";
import PlansTemplateStore from "../templates/PlansTemplateStore";
import SuperUsersStore from "../users/SuperUsersStore";
import UsersStore from "../users/UsersStore";
import SessionStore from "./SessionStore";

export class Stores {
  devsStore: DevsStore;

  usersStore: UsersStore;
  superUsersStore: SuperUsersStore;
  plansStore: PlansStore;
  sectionsStore: SectionsStore;
  activitiesStore: ActivitiesStore;
  templatesStore: PlansTemplateStore;

  // Admin
  businessCustomersStore: BusinessCustomersStore;
  businessCustomerMailsStore: BusinessCustomerMailsStore;
  businessTemplatesStore: BusinessTemplatesStore;

  // Public
  businessPlansStore: BusinessPlansStore;
  businessModulesStore: BusinessModulesStore;
  businessFeedItemsStore: BusinessFeedItemsStore;
  businessFeedCommentsStore: BusinessFeedCommentsStore;
  businessFeedCommentsReplyStore: BusinessFeedCommentsReplyStore;
  businessScheduleStore: BusinessScheduleStore;

  modulesStore: ModulesStore;
  modulesDataStore: ModulesDataStore;

  feedbackStore: FeedbackStore;

  // biome-ignore lint/suspicious/noExplicitAny: Because
  sessionStore: any;

  constructor() {
    this.devsStore = new DevsStore(this);
    this.usersStore = new UsersStore(this);
    this.superUsersStore = new SuperUsersStore(this);
    this.plansStore = new PlansStore(this);
    this.sectionsStore = new SectionsStore(this);
    this.activitiesStore = new ActivitiesStore(this);
    this.feedbackStore = new FeedbackStore(this);
    this.templatesStore = new PlansTemplateStore(this);

    this.businessCustomersStore = new BusinessCustomersStore(this);
    this.businessCustomerMailsStore = new BusinessCustomerMailsStore(this);
    this.businessTemplatesStore = new BusinessTemplatesStore(this);

    this.businessPlansStore = new BusinessPlansStore(this);
    this.businessModulesStore = new BusinessModulesStore(this);
    this.businessFeedItemsStore = new BusinessFeedItemsStore(this);
    this.businessFeedCommentsStore = new BusinessFeedCommentsStore(this);
    this.businessFeedCommentsReplyStore = new BusinessFeedCommentsReplyStore(
      this,
    );
    this.businessScheduleStore = new BusinessScheduleStore(this);

    // Modules with extra data
    this.modulesStore = new ModulesStore(this);
    this.modulesDataStore = new ModulesDataStore(this);

    // App vs Web specific
    this.sessionStore = new SessionStore(this);
  }
}

const StoreContext = createContext<Stores | undefined>(undefined);

// create the provider component
export function StoresProvider({
  children,
  stores,
}: {
  children: ReactNode;
  stores?: Stores;
}) {
  //only create the store once ( store is a singleton)
  const [root] = useState(() => stores ?? new Stores());

  return <StoreContext.Provider value={root}>{children}</StoreContext.Provider>;
}

// create the hook
export function useStores() {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStores must be used within StoresProvider");
  }

  return context;
}
